import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getMyWallet,
  getTopupPackages,
  createTopupOrder,
  getTopupStatus,
  transferPlatformToOperator,
  type TopupPackagePublic,
  type WalletResponse,
} from '../api/fan-api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { TransferSheet } from '../components/TransferSheet';
import { notifyWalletChanged } from '../lib/wallet-bus';

/** Rolls the displayed number toward the target over ~0.6s (cubic
 *  ease-out) so balance changes COUNT up/down like a mechanical
 *  meter instead of teleporting. First render shows the value as-is;
 *  reduced-motion jumps straight to the target. */
function useCountUp(target: number): number {
  const [display, setDisplay] = useState(target);
  const displayRef = useRef(display);
  displayRef.current = display;
  useEffect(() => {
    const from = displayRef.current;
    if (from === target) return;
    const reduced = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setDisplay(target); return; }
    const t0 = performance.now();
    const DUR_MS = 600;
    let raf = 0;
    const step = (now: number): void => {
      const t = Math.min(1, (now - t0) / DUR_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return display;
}

function AnimatedNumber({ value }: { value: number }) {
  const display = useCountUp(value);
  return <>{display.toLocaleString()}</>;
}

function BalanceSkeleton({ label }: { label: string }) {
  return (
    <div
      aria-hidden="true"
      className="rounded-xl border border-slate-800 bg-slate-900 p-4 flex justify-between items-center"
    >
      <div>
        <div className="text-slate-400 text-xs uppercase">{label}</div>
        <div className="h-7 w-24 rounded mt-1.5 nx-skeleton-shimmer" />
      </div>
      <div className="h-7 w-24 rounded-full nx-skeleton-shimmer" />
    </div>
  );
}

export function Wallet() {
  useDocumentTitle('我的錢包');
  const [data, setData] = useState<WalletResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Active transfer-sheet target (operatorId) — null = closed.
  // v4: transfer is platform → operator, not platform → character.
  const [sheetOperator, setSheetOperator] = useState<string | null>(null);
  // Per `ecpay-topup` — package grid + in-flight redirect state.
  const [packages, setPackages] = useState<TopupPackagePublic[] | null>(null);
  const [topupBusy, setTopupBusy] = useState<string | null>(null);  // sku currently redirecting
  // Result banner shown after ECPay redirect back. State machine:
  // null = idle, 'polling' = waiting webhook, 'success' = credited,
  // 'pending-slow' = 10s elapsed, still pending (ATM/CVS may take
  // hours — show "稍後再回來查看"), 'failed' = ECPay returned non-1.
  type ResultState =
    | { kind: 'polling'; tradeNo: string; expectedPoints?: number }
    | { kind: 'success'; pointsCredited: number; amountTwd: number }
    | { kind: 'pending-slow'; tradeNo: string }
    | { kind: 'failed'; tradeNo: string }
    | null;
  const [topupResult, setTopupResult] = useState<ResultState>(null);
  // ?need=<points> — set by CharacterMarket's insufficient-funds modal
  // when the fan bounces here because they couldn't afford an acquire.
  // We use it to highlight the smallest package that covers the gap.
  const [needAmount, setNeedAmount] = useState<number | null>(null);

  async function refresh(): Promise<void> {
    try {
      const out = await getMyWallet();
      setData(out);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const out = await getMyWallet();
        if (alive) setData(out);
      } catch (e) {
        if (alive) setErr((e as Error).message);
      }
    })();
    // Package list — anonymous-OK + cheap, fetched in parallel with
    // the wallet so the grid paints immediately under the platform
    // balance card.
    void getTopupPackages()
      .then((r) => { if (alive) setPackages(r.items); })
      .catch(() => { if (alive) setPackages([]); });
    return () => { alive = false; };
  }, []);

  // Per `ecpay-topup` — when the fan returns from ECPay
  // (?topup=result&tradeNo=NX-...) we poll the status endpoint until
  // it flips to completed/failed, OR we hit the polling budget. The
  // budget is 10 seconds (5 × 2s) — credit-card webhooks usually
  // settle in <2s, ATM/CVS may take hours so we bail to 'pending-slow'.
  // Auto-dismiss success banner after 8 s — the new balance is
  // already visible on the platform card immediately below, so
  // leaving the banner sticky just clutters the page. Failed /
  // pending-slow stay until the fan dismisses (they're actionable).
  useEffect(() => {
    if (topupResult?.kind !== 'success') return;
    const t = window.setTimeout(() => {
      setTopupResult(null);
      // Clear the query so a reload doesn't re-trigger polling.
      window.history.replaceState({}, '', '/wallet');
    }, 8000);
    return () => window.clearTimeout(t);
  }, [topupResult]);

  // pending-slow recheck — manual one-shot status poll. Pressed from
  // the banner when ATM/CVS payment takes longer than the auto-poll
  // budget. If the webhook has now credited, flip to success +
  // refresh wallet + notify pill. Otherwise just transient feedback.
  const [rechecking, setRechecking] = useState(false);
  async function recheckPendingTopup(): Promise<void> {
    if (rechecking || !topupResult || topupResult.kind !== 'pending-slow') return;
    const tradeNo = topupResult.tradeNo;
    setRechecking(true);
    try {
      const s = await getTopupStatus(tradeNo);
      // Backend writes 'success' (see fan-topup.ts webhook); 'completed'
      // kept for belt-and-braces with any older order rows.
      if (s.status === 'success' || s.status === 'completed') {
        setTopupResult({ kind: 'success', pointsCredited: s.pointsCredited, amountTwd: s.amountTwd });
        void refresh();
        notifyWalletChanged();
      } else if (s.status === 'failed') {
        setTopupResult({ kind: 'failed', tradeNo });
      }
      // Still pending → leave the banner as-is. The button's
      // disable-while-rechecking spinner is enough feedback.
    } catch { /* network blip — banner stays */ }
    finally { setRechecking(false); }
  }

  // Parse ?need=<points> once on mount. Cleared from the URL once the
  // banner is dismissed or a package is tapped — see buyPackage / the
  // dismiss button.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('need');
    if (raw) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n > 0) setNeedAmount(n);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('topup') !== 'result') return;
    const tradeNo = params.get('tradeNo');
    if (!tradeNo) return;
    setTopupResult({ kind: 'polling', tradeNo });
    // On a mobile device the polling banner sits above the platform
    // balance card; if the fan's viewport is somewhere mid-page (e.g.
    // they scrolled before tapping the topup card) the banner is off-
    // screen when they return from ECPay. Snap to top so the result
    // is the first thing they see.
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* old browser */ }
    let cancelled = false;
    let attempt = 0;
    const MAX_ATTEMPTS = 5;
    const POLL_INTERVAL_MS = 2000;
    const tick = async (): Promise<void> => {
      if (cancelled) return;
      try {
        const s = await getTopupStatus(tradeNo);
        if (cancelled) return;
        if (s.status === 'success' || s.status === 'completed') {
          setTopupResult({ kind: 'success', pointsCredited: s.pointsCredited, amountTwd: s.amountTwd });
          // Refresh local data + tell the header pill so the new
          // balance shows up immediately instead of waiting for the
          // 60s tick.
          void refresh();
          notifyWalletChanged();
          return;
        }
        if (s.status === 'failed') {
          setTopupResult({ kind: 'failed', tradeNo });
          return;
        }
      } catch { /* network blip — retry */ }
      attempt += 1;
      if (attempt >= MAX_ATTEMPTS) {
        if (!cancelled) setTopupResult({ kind: 'pending-slow', tradeNo });
        return;
      }
      setTimeout(() => { void tick(); }, POLL_INTERVAL_MS);
    };
    void tick();
    return () => { cancelled = true; };
  }, []);

  /**
   * Tap a package card → POST /topup/create → server returns ECPay
   * AIO checkout form params → build a hidden form + submit. Browser
   * then redirects to ECPay's hosted payment page; on completion
   * ECPay POSTs back to /api/ecpay/notify (webhook) and redirects the
   * browser to /wallet?topup=result.
   */
  async function buyPackage(sku: string): Promise<void> {
    if (topupBusy) return;
    setTopupBusy(sku);
    setErr(null);
    try {
      const out = await createTopupOrder(sku);
      // Build form dynamically — works on every browser, no third-
      // party SDK. Hidden form gets attached + submitted then
      // immediately removed.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = out.url;
      form.style.display = 'none';
      for (const [k, v] of Object.entries(out.params)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setTopupBusy(null);
      setErr((e as Error).message);
    }
  }

  const activeOperator = sheetOperator
    ? data?.operators.find((o) => o.operatorId === sheetOperator)
    : null;

  // Smallest active package whose points cover the shortfall — used
  // both for the banner copy and to highlight the matching card.
  const recommendedSku: string | null = (() => {
    if (!needAmount || !packages) return null;
    const sorted = [...packages].sort((a, b) => a.points - b.points);
    const match = sorted.find((p) => p.points >= needAmount);
    return match?.sku ?? sorted[sorted.length - 1]?.sku ?? null;
  })();

  function dismissNeed(): void {
    setNeedAmount(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('need');
    window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
  }

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-10">
      <h1 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">我的錢包</h1>
      {err && <div role="alert" className="text-red-400 text-sm">{err}</div>}

      {/* Per `ecpay-topup` — post-redirect result banner. Dismissable. */}
      {topupResult && (
        <div
          role="status"
          className={`rounded-xl border p-4 text-sm mb-3 ${
            topupResult.kind === 'success'
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100'
              : topupResult.kind === 'failed'
                ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
                : 'border-violet-500/40 bg-violet-500/10 text-violet-100'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              {topupResult.kind === 'polling' && (
                <>
                  <div className="font-medium">處理中…</div>
                  <div className="text-xs opacity-80 mt-0.5">
                    向綠界確認你的付款狀態，通常 2-5 秒。
                  </div>
                </>
              )}
              {topupResult.kind === 'success' && (
                <>
                  <div className="font-medium">儲值成功 🎉</div>
                  <div className="text-xs opacity-90 mt-0.5">
                    感謝你的支持！NT$ {topupResult.amountTwd.toLocaleString()} 已入帳 <span className="font-semibold tabular-nums">{topupResult.pointsCredited.toLocaleString()}</span> 點，快去和喜歡的角色互動吧 ✨
                  </div>
                </>
              )}
              {topupResult.kind === 'pending-slow' && (
                <>
                  <div className="font-medium">付款處理中</div>
                  <div className="text-xs opacity-80 mt-0.5">
                    ATM / 超商代碼通常需要 30 分鐘至數小時才會入帳，完成後點數會自動進到你的錢包。訂單編號：<span className="font-mono">{topupResult.tradeNo}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { void recheckPendingTopup(); }}
                    disabled={rechecking}
                    className="mt-2 inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/15 px-3 py-1 text-xs text-violet-100 hover:bg-violet-500/25 disabled:opacity-60 transition"
                  >
                    {rechecking ? '查詢中…' : '🔄 再查一次'}
                  </button>
                </>
              )}
              {topupResult.kind === 'failed' && (
                <>
                  <div className="font-medium">付款未完成</div>
                  <div className="text-xs opacity-80 mt-0.5">
                    可能是取消、退刷或卡片驗證失敗。可以再試一次。訂單編號：<span className="font-mono">{topupResult.tradeNo}</span>
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setTopupResult(null);
                // Drop the query params so reload doesn't re-poll.
                window.history.replaceState({}, '', '/wallet');
              }}
              aria-label="關閉"
              className="shrink-0 rounded-full px-2 py-0.5 text-sm opacity-70 hover:opacity-100"
            >✕</button>
          </div>
        </div>
      )}

      {!err && !data && (
        <div className="space-y-3" aria-live="polite" aria-busy="true">
          <BalanceSkeleton label="平台" />
          <BalanceSkeleton label="角色" />
        </div>
      )}

      {data && (
        <div className="space-y-3">
          {/* Platform wallet (黑耀石) */}
          <div className="rounded-xl border border-violet-500/40 bg-slate-900 p-4 flex justify-between items-center">
            <div>
              <div className="text-violet-300 text-xs uppercase tracking-wider">平台 · 黑耀石</div>
              <div className="text-2xl font-semibold">
                <AnimatedNumber value={data.platform.balance} />{' '}
                <span className="text-sm text-slate-500">點</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">用於解鎖 skin / 物件 / 轉到角色錢包</div>
            </div>
          </div>

          {/* Per `ecpay-topup` — package grid. Active packages only,
              sorted by displayOrder. Tap → ECPay AIO Checkout. */}
          {packages && packages.length > 0 && (
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-violet-300 text-xs uppercase tracking-wider">儲值方案</div>
                {needAmount && (
                  <button
                    type="button"
                    onClick={dismissNeed}
                    className="text-[10px] text-slate-500 hover:text-slate-300"
                    aria-label="關閉建議"
                  >× 清除建議</button>
                )}
              </div>
              {needAmount && (
                <div role="status" className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-100 leading-relaxed">
                  解鎖角色還差 <span className="font-semibold tabular-nums">{needAmount.toLocaleString()}</span> 點。
                  {recommendedSku && <span className="opacity-90"> 已為你標出最划算的方案。</span>}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {packages.map((p) => {
                  const bonus = p.points - p.amountNtd;
                  const bonusPct = p.amountNtd > 0 ? Math.round((bonus / p.amountNtd) * 100) : 0;
                  const busy = topupBusy === p.sku;
                  const isRecommended = p.sku === recommendedSku;
                  return (
                    <button
                      key={p.sku}
                      type="button"
                      onClick={() => { void buyPackage(p.sku); }}
                      disabled={!!topupBusy}
                      className={`relative rounded-xl border p-3 text-left transition focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-60 ${
                        isRecommended
                          ? 'border-amber-400 bg-amber-500/10 ring-2 ring-amber-400/70 shadow-[0_0_18px_-4px_rgba(251,191,36,0.45)]'
                          : p.label
                            ? 'border-violet-500/60 bg-violet-500/10 hover:bg-violet-500/20'
                            : 'border-slate-700 bg-slate-900/80 hover:border-violet-500/40 hover:bg-slate-800'
                      }`}
                    >
                      {isRecommended && (
                        <span className="absolute -top-1.5 -left-1.5 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-semibold text-slate-900 shadow">
                          推薦
                        </span>
                      )}
                      {p.label && (
                        <span className="absolute -top-1.5 -right-1.5 rounded-full bg-violet-500 px-2 py-0.5 text-[10px] font-medium text-white shadow">
                          {p.label}
                        </span>
                      )}
                      <div className="text-xs text-slate-400">NT$</div>
                      <div className="text-2xl font-semibold text-slate-100 leading-tight tabular-nums">
                        {p.amountNtd.toLocaleString()}
                      </div>
                      <div className="mt-1.5 text-sm text-violet-300 tabular-nums">
                        {p.points.toLocaleString()} 點
                      </div>
                      {bonus > 0 && (
                        <div className="mt-0.5 text-[11px] text-amber-300">
                          {p.badge ?? `加碼 ${bonusPct}%`}
                        </div>
                      )}
                      {busy && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-slate-950/70 text-xs text-violet-200">
                          前往綠界…
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-slate-500 leading-relaxed">
                付款由綠界 ECPay 處理。所有面額皆為新台幣，加碼點數以實際入帳為準。
              </div>
            </div>
          )}

          {/* v4 operator wallets — primary chat-pay scope.
              Per-operator card with currency name + icon; characters
              listed as a small icon strip below (informational, the
              balance is shared across all of them). */}
          <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/30 p-3 text-[11px] text-slate-500 leading-relaxed">
            <span className="text-slate-300 font-medium">經營者錢包：</span>
            每個經營者一條錢包；他旗下任一角色聊天都扣同一條。
            從上方「平台 · 黑耀石」按「轉入」加值到對應經營者，幣別由經營者自訂。
          </div>

          {data.operators.length === 0 && (
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4">
              <div className="text-sm text-slate-200">尚未加入任何角色</div>
              <div className="text-[12px] text-slate-400 mt-1 leading-relaxed">
                到探索頁加入角色後，他所屬的經營者錢包會出現在這裡。
              </div>
              <Link
                to="/"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-violet-500 px-4 py-2 text-white text-sm font-medium hover:bg-violet-400 transition"
              >
                <span>探索角色</span>
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          )}

          {/* Operator cards — sorted by balance desc, name as tiebreak. */}
          {[...data.operators]
            .sort((a, b) => (b.balance - a.balance) || a.operatorDisplayName.localeCompare(b.operatorDisplayName))
            .map((op) => (
              <div
                key={op.operatorId}
                className="rounded-xl border border-slate-800 bg-slate-900 p-4 space-y-3"
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    {/* Operator company name — primary surface. */}
                    <div className="text-base font-semibold text-slate-100 truncate">
                      {op.operatorDisplayName}
                    </div>
                    {/* Balance row with operator-defined currency + icon. */}
                    <div className="mt-1.5 flex items-center gap-1.5">
                      {op.currencyIconUrl && (
                        <img
                          src={op.currencyIconUrl}
                          alt=""
                          aria-hidden="true"
                          className="h-5 w-5 object-contain"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <span className="text-2xl font-semibold tabular-nums">
                        <AnimatedNumber value={op.balance} />
                      </span>
                      <span className="text-xs text-slate-500">{op.currencyName}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSheetOperator(op.operatorId)}
                    disabled={data.platform.balance <= 0}
                    title={data.platform.balance <= 0 ? '平台點數不足，請先儲值' : `從平台轉點數到「${op.operatorDisplayName}」錢包`}
                    className="shrink-0 rounded-full bg-violet-500 px-4 py-2 text-white text-sm font-medium hover:bg-violet-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    轉入
                  </button>
                </div>
                {/* Character thumbnail strip — info only, no per-character
                    balance. Conveys "this wallet covers these characters". */}
                {op.characters.length > 0 && (
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-800/60">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 shrink-0">旗下角色</span>
                    <div className="flex flex-wrap gap-1.5">
                      {op.characters.map((c) => (
                        <Link
                          key={c.characterId}
                          to={`/c/${encodeURIComponent(c.characterId)}`}
                          title={c.characterName}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-900/80 px-2 py-0.5 hover:border-violet-500/40 hover:bg-slate-800 transition"
                        >
                          {c.thumbnailUrl ? (
                            <img
                              src={c.thumbnailUrl}
                              alt=""
                              aria-hidden="true"
                              className="h-4 w-4 rounded-full object-cover"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <span className="h-4 w-4 rounded-full bg-slate-700" aria-hidden="true" />
                          )}
                          <span className="text-[11px] text-slate-300 max-w-[6rem] truncate">{c.characterName}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

          {/* Orphan characters — owned but no operator bound. */}
          {data.orphanCharacters && data.orphanCharacters.length > 0 && (
            <div className="rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200/80">
              下列角色尚未綁定經營者，無法接受轉入：
              {data.orphanCharacters.map((c) => c.characterName).join('、')}
            </div>
          )}

          {/* Legacy group wallets — read-only */}
          {data.groups.length > 0 && (
            <>
              <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/30 p-3 text-[11px] text-slate-500">
                舊版群組錢包（不再用於對話扣點，僅顯示餘額供查詢）
              </div>
              {data.groups.map((g) => (
                <div
                  key={g.groupId}
                  className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 flex justify-between items-center opacity-70"
                >
                  <div>
                    <div className="text-slate-400 text-xs uppercase">群組 · {g.groupName || g.groupId}</div>
                    <div className="text-xl font-semibold">
                      <AnimatedNumber value={g.balance} />{' '}
                      <span className="text-sm text-slate-500">點 (舊版)</span>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
      {/* Bottom-sheet transfer flow — platform → operator. */}
      {data && activeOperator && (
        <TransferSheet
          open={!!sheetOperator}
          onClose={() => setSheetOperator(null)}
          targetName={activeOperator.operatorDisplayName}
          currencyName={activeOperator.currencyName}
          currencyIconUrl={activeOperator.currencyIconUrl}
          platformBalance={data.platform.balance}
          targetBalance={activeOperator.balance}
          onConfirm={async (amount) => {
            await transferPlatformToOperator(activeOperator.operatorId, amount);
            await refresh();
            // Header pill cares about platform balance — operator
            // transfer just debited it.
            notifyWalletChanged();
          }}
        />
      )}
    </div>
  );
}
